import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell, ResponsiveContainer,
} from 'recharts';
import { api } from '../hooks/useApi';
import Icon from '../components/Icon';
import './InventaireProduits.css';
import '../components/ConfirmDeleteModal.css';

// ─── Constantes ───────────────────────────────
const ONGLETS = [
  { key: 'tous',         label: 'Tous' },
  { key: 'porc',         label: 'Porc' },
  { key: 'saucisses',    label: 'Saucisses' },
  { key: 'cotes_levees', label: 'Côtes Levées' },
  { key: 'saumon',       label: 'Saumon' },
  { key: 'brisket',      label: 'Brisket' },
  { key: 'sauce',        label: 'Sauce BBQ' },
];

const PRODUIT_LABELS = {
  porc:         'Porc Effiloché',
  saucisses:    'Saucisses',
  cotes_levees: 'Côtes Levées',
  saumon:       'Saumon Fumé',
  brisket:      'Brisket',
  burnt_ends:   'Burnt Ends',
  sauce:        'Sauce BBQ',
  ribs:         'Ribs',
};

// ─── Helpers ──────────────────────────────────
function fmt$(v) {
  if (v == null || v === '') return '—';
  return `${parseFloat(v).toFixed(2)}$`;
}

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('fr-CA', { year: 'numeric', month: 'short', day: 'numeric' });
}

function fmtTemps(syncTime) {
  if (!syncTime) return '—';
  const mins = Math.floor((Date.now() - syncTime.getTime()) / 60000);
  if (mins === 0) return "à l'instant";
  return `il y a ${mins} min`;
}

// Utilise les tokens CSS pour les couleurs de marge (pas de couleurs en dur)
function couleurMarge(pct) {
  if (pct == null) return 'var(--muted)';
  if (pct >= 65) return 'var(--success)';
  if (pct >= 40) return 'var(--warning)';
  return 'var(--danger)';
}

// Recharts Cell accepte uniquement des string hex/rgb — pas de var() CSS
// On utilise les valeurs absolues correspondant aux tokens du thème sombre
function couleurBarreMarge(pct) {
  if (pct >= 65) return '#16a34a'; // --success
  if (pct >= 40) return '#d97706'; // --warning
  return '#c0392b';                // --danger
}

// ─── Modal d'ajustement manuel du stock ──────
function ModalAjusterStock({ ligne, sku, onConfirm, onAnnuler, loading }) {
  const [vendu, setVendu] = useState(String(ligne.quantite_vendue ?? 0));
  const [raison, setRaison] = useState('');
  const [erreur, setErreur] = useState('');

  const dispoActuel = ligne.quantite - (ligne.quantite_vendue || 0);
  const dispoNouveau = ligne.quantite - (parseInt(vendu, 10) || 0);

  const submit = () => {
    setErreur('');
    const v = parseInt(vendu, 10);
    if (!Number.isFinite(v) || v < 0) return setErreur('Quantité vendue invalide');
    if (v > ligne.quantite) return setErreur(`Max ${ligne.quantite} (= total produit)`);
    if (!raison.trim() || raison.trim().length < 3) return setErreur('Raison requise (min 3 caractères)');
    onConfirm({ quantite_vendue: v, raison: raison.trim() });
  };

  return (
    <div className="cdm-overlay" onClick={onAnnuler}>
      <div className="cdm-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 460 }}>
        <h3 className="cdm-titre">
          Ajuster le stock — <span className="gold">{sku}</span>
        </h3>
        <p className="muted" style={{ fontSize: 12, marginBottom: 12 }}>
          Batch <strong>{ligne.batch_nom}</strong> — total produit : <strong>{ligne.quantite}</strong>
        </p>

        <div style={{ display: 'grid', gap: 'var(--space-3)', marginBottom: 'var(--space-4)' }}>
          <div className="field">
            <label>Nouvelle quantité vendue</label>
            <input
              type="number"
              min="0"
              max={ligne.quantite}
              value={vendu}
              onChange={e => setVendu(e.target.value)}
            />
            <span className="field-hint">
              Disponible actuellement : {dispoActuel} → après ajustement : <span className="gold" style={{ fontFeatureSettings: "'tnum' 1" }}>{dispoNouveau}</span>
            </span>
          </div>

          <div className="field">
            <label>Raison (obligatoire pour traçabilité)</label>
            <textarea
              value={raison}
              onChange={e => setRaison(e.target.value)}
              rows={2}
              placeholder="Ex : annulation commande test, correction inventaire physique…"
            />
          </div>

          {erreur && (
            <div className="error-inline">
              <Icon name="erreur" size={14} />
              <span>{erreur}</span>
            </div>
          )}
        </div>

        <p className="muted" style={{ fontSize: 12, marginBottom: 'var(--space-4)' }}>
          Note : cet ajustement modifie le stock local uniquement. Le journal des imputations FIFO
          reste intact — une re-synchronisation des commandes ne ré-imputera pas ce que vous avez
          déjà retiré.
        </p>

        <div className="cdm-actions">
          <button className="btn btn-ghost" onClick={onAnnuler} disabled={loading}>Annuler</button>
          <button className="btn btn-primary" onClick={submit} disabled={loading}>
            {loading ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Statut badge ─────────────────────────────
function StatutBadge({ p }) {
  if (!p.wc_existe) return (
    <span className="ip-badge ip-badge--absent"><Icon name="attention" size={10} /> Absent WC</span>
  );
  const wcStock  = p.wc_stock  ?? 0;
  const appStock = p.app_stock_disponible ?? p.app_stock_total ?? 0;
  if (wcStock !== appStock) return (
    <span className="ip-badge ip-badge--ecart"><Icon name="alerte" size={10} /> Écart</span>
  );
  return <span className="ip-badge ip-badge--ok"><Icon name="check" size={10} /> OK</span>;
}

// ─── Panneau détail ───────────────────────────
function PanneauDetail({ produit, onClose, onAjustement }) {
  const [ligneAjuster, setLigneAjuster] = useState(null);
  const [enregistrement, setEnregistrement] = useState(false);
  const [erreurAjustement, setErreurAjustement] = useState('');

  if (!produit) return null;

  const margeVal = produit.valeur_stock_vente - produit.valeur_stock_cout;

  const enregistrerAjustement = async ({ quantite_vendue, raison }) => {
    setEnregistrement(true);
    setErreurAjustement('');
    try {
      await api.post(`/inventaire/produits/${ligneAjuster.inventaire_produit_id}/ajuster-vendu`, {
        quantite_vendue, raison,
      });
      setLigneAjuster(null);
      if (onAjustement) onAjustement();
    } catch (err) {
      setErreurAjustement(err.response?.data?.error || err.message);
    } finally {
      setEnregistrement(false);
    }
  };

  return (
    <div className="ip-overlay" onClick={onClose}>
      <div className="ip-panneau" onClick={e => e.stopPropagation()}>

        <div className="ip-panneau__header">
          <div>
            <h3 className="ip-panneau__titre">{produit.nom}</h3>
            <code className="ip-panneau__sku muted">{produit.sku}</code>
          </div>
          <button className="btn btn-ghost ip-panneau__close btn--sm" onClick={onClose} aria-label="Fermer">
            <Icon name="fermer" size={14} />
          </button>
        </div>

        {/* Détail produit */}
        <div className="ip-panneau__section">
          <div className="ip-panneau__section-header">
            <Icon name="package" size={14} color="var(--muted)" />
            <h4>Détail produit</h4>
          </div>
          <div className="ip-panneau__grid">
            <span className="muted">SKU</span>
            <span>{produit.sku}</span>
            <span className="muted">Prix vente WC</span>
            <span className="gold">{fmt$(produit.wc_prix)}</span>
            <span className="muted">Stock WooCommerce</span>
            <span>{produit.wc_stock ?? '—'}</span>
            <span className="muted">Stock App</span>
            <span>
              {produit.app_stock_disponible ?? produit.app_stock_total}
              {produit.app_stock_vendu > 0 && (
                <span className="muted" style={{ marginLeft: 6, fontSize: 12 }}>
                  ({produit.app_stock_total} produit{produit.app_stock_total !== 1 ? 's' : ''} − {produit.app_stock_vendu} vendu{produit.app_stock_vendu !== 1 ? 's' : ''})
                </span>
              )}
            </span>
            <span className="muted">Statut WC</span>
            <span>{produit.wc_statut || '—'}</span>
            <span className="muted">Type</span>
            <span>{produit.type === 'permanent' ? 'Produit permanent' : 'Produit unique'}</span>
          </div>
          {produit.wc_existe && (
            <a
              href={`https://escamel.ca/product/${produit.sku.toLowerCase()}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-ghost btn--sm"
              style={{ marginTop: 'var(--space-3)', display: 'inline-flex', gap: 'var(--space-2)' }}
            >
              <Icon name="boutique" size={13} />
              Voir sur escamel.ca
            </a>
          )}
        </div>

        {/* Stock FIFO par batch */}
        {produit.batches_en_stock && produit.batches_en_stock.length > 0 && (
          <div className="ip-panneau__section">
            <div className="ip-panneau__section-header">
              <Icon name="liste" size={14} color="var(--muted)" />
              <h4>Stock par batch (FIFO)</h4>
            </div>
            <div className="ip-panneau__table-wrap">
              <table className="esc-table">
                <thead>
                  <tr>
                    <th>Priorité</th>
                    <th>Batch</th>
                    <th>Date prod.</th>
                    <th>Dispo</th>
                    <th>Vendu</th>
                    <th>Coût unit.</th>
                    <th>Marge</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {produit.batches_en_stock.map(b => (
                    <tr key={b.batch_id}>
                      <td>
                        {b.fifo_priorite === 1
                          ? <span className="ip-badge ip-badge--fifo">FIRST</span>
                          : b.fifo_priorite}
                      </td>
                      <td>{b.batch_nom}</td>
                      <td>{fmtDate(b.date_prod)}</td>
                      <td>{b.quantite_dispo}{b.quantite ? <span className="muted" style={{ fontSize: 11 }}> /{b.quantite}</span> : null}</td>
                      <td className="muted">{b.quantite_vendue ?? 0}</td>
                      <td>{fmt$(b.cout_unitaire)}</td>
                      <td style={{ color: couleurMarge(b.marge_pct) }}>
                        {b.marge_pct != null ? `${b.marge_pct}%` : '—'}
                      </td>
                      <td>
                        {b.inventaire_produit_id && (
                          <button
                            className="btn btn-ghost btn--sm"
                            onClick={() => setLigneAjuster(b)}
                            title="Ajuster manuellement la quantité vendue"
                          >
                            <Icon name="modifier" size={12} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Financier */}
        <div className="ip-panneau__section">
          <div className="ip-panneau__section-header">
            <Icon name="argent" size={14} color="var(--muted)" />
            <h4>Financier</h4>
          </div>
          <div className="ip-panneau__grid">
            <span className="muted">Valeur au prix de vente</span>
            <span className="gold">{fmt$(produit.valeur_stock_vente)}</span>
            <span className="muted">Valeur au coût</span>
            <span>{fmt$(produit.valeur_stock_cout)}</span>
            <span className="muted">Marge brute</span>
            <span style={{ color: couleurMarge(produit.marge_pct) }}>
              {fmt$(margeVal)}
              {produit.marge_pct != null && ` (${produit.marge_pct}%)`}
            </span>
          </div>
        </div>

        {erreurAjustement && (
          <div className="error-inline" style={{ margin: 'var(--space-3) 0' }}>
            <Icon name="erreur" size={14} />
            <span>{erreurAjustement}</span>
          </div>
        )}

      </div>

      {ligneAjuster && (
        <ModalAjusterStock
          ligne={ligneAjuster}
          sku={produit.sku}
          onConfirm={enregistrerAjustement}
          onAnnuler={() => { setLigneAjuster(null); setErreurAjustement(''); }}
          loading={enregistrement}
        />
      )}
    </div>
  );
}

// ─── Composant principal ──────────────────────
export default function InventaireProduits() {
  const [donnees,    setDonnees]    = useState(null);
  const [marges,     setMarges]     = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [erreur,     setErreur]     = useState(null);
  const [syncing,    setSyncing]    = useState(false);
  const [toast,      setToast]      = useState(null);
  const [onglet,     setOnglet]     = useState('tous');
  const [selection,  setSelection]  = useState(null);
  const [syncTime,   setSyncTime]   = useState(null);

  const charger = useCallback(async () => {
    setLoading(true);
    setErreur(null);
    try {
      const [d, m] = await Promise.all([
        api.get('/inventaire/produits/complet'),
        api.get('/inventaire/produits/marge'),
      ]);
      setDonnees(d);
      setMarges(m);
      setSyncTime(new Date());
    } catch (err) {
      setErreur(err.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  // Auto-dismiss toast
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  const syncWC = async () => {
    setSyncing(true);
    try {
      const r = await api.post('/inventaire/produits/sync-wc', {});
      setToast({ type: 'success', message: `${r.synced} produit(s) synchronisé(s) ✓` });
      await charger();
    } catch (err) {
      setToast({ type: 'error', message: err.message || 'Erreur synchronisation' });
    } finally {
      setSyncing(false);
    }
  };

  const produitsFiltres = (donnees?.produits || []).filter(
    p => onglet === 'tous' || p.produit === onglet
  );

  // ── Rendu chargement / erreur ──
  if (loading && !donnees) {
    return (
      <div className="ip">
        <div className="ip__header"><div><div className="skeleton" style={{ height: 36, width: 280, borderRadius: 'var(--radius-sm)' }} /></div></div>
        <div className="ip-kpi-grid">
          {[0,1,2,3].map(i => (
            <div key={i} className="skeleton" style={{ height: 88, borderRadius: 'var(--radius-lg)', animationDelay: `${i * 60}ms` }} />
          ))}
        </div>
        <div className="skeleton" style={{ height: 44, marginBottom: 'var(--space-3)', borderRadius: 'var(--radius-md)' }} />
        <div className="skeleton" style={{ height: 300, borderRadius: 'var(--radius-lg)' }} />
      </div>
    );
  }
  if (erreur && !donnees) {
    return (
      <div className="ip">
        <div className="ip__header"><h1 className="ip__title">Inventaire — Produits Finis</h1></div>
        <div className="error-inline">
          <Icon name="erreur" size={16} />
          <span>{erreur}</span>
          <button className="btn btn-primary btn--sm" style={{ marginLeft: 'auto' }} onClick={charger}>Réessayer</button>
        </div>
      </div>
    );
  }

  return (
    <div className="ip">

      {toast && (
        <div className={`ip-toast ip-toast--${toast.type}`}>{toast.message}</div>
      )}

      {/* En-tête */}
      <div className="ip__header">
        <div>
          <h1 className="ip__title">Inventaire — Produits Finis</h1>
          <p className="ip__subtitle">Stock WooCommerce, marges et suivi FIFO</p>
        </div>
      </div>

      {/* KPI résumé */}
      <div className="ip-kpi-grid rise-in">
        <div className="ip-kpi-card">
          <span className="ip-kpi-label">Valeur totale (vente)</span>
          <span className="ip-kpi-value ip-kpi-value--accent">{fmt$(donnees?.resume?.valeur_totale_vente)}</span>
        </div>
        <div className="ip-kpi-card">
          <span className="ip-kpi-label">Marge moyenne</span>
          <span className="ip-kpi-value" style={{ color: couleurMarge(donnees?.resume?.marge_moyenne_pct) }}>
            {donnees?.resume?.marge_moyenne_pct ?? '—'}%
          </span>
        </div>
        <div className="ip-kpi-card">
          <span className="ip-kpi-label">SKUs avec alerte</span>
          <span className={`ip-kpi-value ${(donnees?.resume?.nb_alertes_sku || 0) > 0 ? 'ip-kpi-value--danger' : 'ip-kpi-value--success'}`}>
            {donnees?.resume?.nb_alertes_sku ?? 0}
          </span>
        </div>
        <div className="ip-kpi-card">
          <span className="ip-kpi-label">Dernière sync</span>
          <span className="ip-kpi-value" style={{ fontSize: 16 }}>{fmtTemps(syncTime)}</span>
        </div>
      </div>

      {/* Toolbar sync */}
      <div className="ip-toolbar">
        <div className="ip-toolbar__left">
          <span className="ip-sync-info">
            <Icon name="rafraichir" size={13} />
            Synchronisez pour mettre à jour les stocks WooCommerce
          </span>
        </div>
        <button
          className="btn btn-primary"
          onClick={syncWC}
          disabled={syncing}
        >
          <Icon name={syncing ? 'rafraichir' : 'rafraichir'} size={14} />
          {syncing ? 'Synchronisation…' : 'Synchroniser WC'}
        </button>
      </div>

      {/* Alertes SKU */}
      {donnees?.alertes?.length > 0 && (
        <div className="ip-alertes">
          <div className="ip-alertes__header">
            <Icon name="attention" size={15} color="var(--danger)" />
            <h3 className="ip-alertes__title">SKUs introuvables sur WooCommerce</h3>
          </div>
          <ul className="ip-alertes__liste">
            {donnees.alertes.map((a, i) => (
              <li key={i}>
                <code>{a.sku}</code> —{' '}
                {a.quantite_app} unité{a.quantite_app !== 1 ? 's' : ''} dans l'app, absent sur WC
                {a.batch_nom && <span className="muted"> (batch : {a.batch_nom})</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Onglets */}
      <div className="ip-onglets">
        {ONGLETS.map(o => (
          <button
            key={o.key}
            className={`ip-onglet${onglet === o.key ? ' ip-onglet--actif' : ''}`}
            onClick={() => setOnglet(o.key)}
          >
            {o.label}
          </button>
        ))}
      </div>

      {/* Tableau principal */}
      <div className="ip-card-table">
        <div className="ip-table-wrap">
          <table className="esc-table ip-table">
            <thead>
              <tr>
                <th>SKU</th>
                <th>Produit</th>
                <th>Stock WC</th>
                <th>Stock App</th>
                <th>Valeur vente</th>
                <th>Marge</th>
                <th>Batch prioritaire</th>
                <th>Statut</th>
              </tr>
            </thead>
            <tbody>
              {produitsFiltres.length === 0 ? (
                <tr>
                  <td colSpan={8} style={{ padding: 0 }}>
                    <div className="empty-state">
                      <Icon name="package" size={36} />
                      <p className="empty-state__titre">Aucun produit dans cet onglet</p>
                      <p>Synchronisez WooCommerce ou sélectionnez un autre filtre.</p>
                    </div>
                  </td>
                </tr>
              ) : (
                produitsFiltres.map(p => {
                  const batchPrio = p.batches_en_stock?.[0];
                  return (
                    <tr
                      key={p.sku}
                      className="ip-table__row"
                      onClick={() => setSelection(p)}
                    >
                      <td><code>{p.sku}</code></td>
                      <td>
                        {p.nom}
                        {p.type === 'unique' && (
                          <span className="ip-badge ip-badge--unique">Unique</span>
                        )}
                      </td>
                      <td style={{ fontFeatureSettings: "'tnum' 1" }}>{p.wc_stock ?? '—'}</td>
                      <td style={{ fontFeatureSettings: "'tnum' 1" }}>{p.app_stock_disponible ?? p.app_stock_total}</td>
                      <td className="gold" style={{ fontFeatureSettings: "'tnum' 1" }}>{fmt$(p.valeur_stock_vente)}</td>
                      <td style={{ color: couleurMarge(p.marge_pct), fontFeatureSettings: "'tnum' 1" }}>
                        {p.marge_pct != null ? `${p.marge_pct}%` : '—'}
                      </td>
                      <td className="muted">
                        {batchPrio
                          ? `${batchPrio.batch_nom} (${batchPrio.quantite_dispo} u.)`
                          : '—'}
                      </td>
                      <td><StatutBadge p={p} /></td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Section marges */}
      {marges?.par_produit?.length > 0 && (
        <div className="card ip-marges rise-in">
          <div className="ip-marges__header">
            <Icon name="analytics" size={15} color="var(--muted)" />
            <h2 className="ip-marges__title">Marges par produit</h2>
          </div>
          <div className="ip-marges__layout">

            <div className="ip-marges__chart">
              <ResponsiveContainer width="100%" height={280}>
                <BarChart
                  data={marges.par_produit}
                  layout="vertical"
                  margin={{ top: 4, right: 24, bottom: 4, left: 90 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis
                    type="number"
                    domain={[0, 100]}
                    tickFormatter={v => `${v}%`}
                    stroke="var(--muted)"
                    tick={{ fill: 'var(--muted)', fontSize: 12 }}
                  />
                  <YAxis
                    type="category"
                    dataKey="produit"
                    tickFormatter={v => PRODUIT_LABELS[v] || v}
                    stroke="var(--muted)"
                    tick={{ fill: 'var(--muted)', fontSize: 12 }}
                    width={90}
                  />
                  <Tooltip
                    formatter={v => [`${v}%`, 'Marge']}
                    contentStyle={{
                      background: 'var(--card)',
                      border: '1px solid var(--border)',
                      borderRadius: 6,
                      color: 'var(--text)',
                    }}
                  />
                  <Bar dataKey="marge_moyenne" name="Marge" radius={[0, 4, 4, 0]}>
                    {marges.par_produit.map((entry, i) => (
                      <Cell key={i} fill={couleurBarreMarge(entry.marge_moyenne)} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div>
              <table className="esc-table">
                <thead>
                  <tr>
                    <th>Produit</th>
                    <th>Marge</th>
                    <th>Revenu total</th>
                    <th>Coût total</th>
                  </tr>
                </thead>
                <tbody>
                  {marges.par_produit.map((p, i) => (
                    <tr key={i}>
                      <td>{PRODUIT_LABELS[p.produit] || p.produit}</td>
                      <td style={{ color: couleurMarge(p.marge_moyenne), fontFeatureSettings: "'tnum' 1" }}>
                        <strong>{p.marge_moyenne}%</strong>
                      </td>
                      <td className="gold" style={{ fontFeatureSettings: "'tnum' 1" }}>{fmt$(p.revenu_total)}</td>
                      <td style={{ fontFeatureSettings: "'tnum' 1" }}>{fmt$(p.cout_total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

          </div>
        </div>
      )}

      <PanneauDetail
        produit={selection}
        onClose={() => setSelection(null)}
        onAjustement={() => { charger(); setSelection(null); }}
      />

    </div>
  );
}
